/**
 * Product Page JavaScript
 * The Color — thecolor.uk
 *
 * Handles:
 * - Variant selection and price/availability updates (RRP label, saving
 *   pills, per-size spans, sold-out note with the nearest in-stock size)
 * - Image gallery and thumbnail switching
 * - Image zoom modal (prev/next, counter, focus trap, scroll lock)
 * - Quantity controls (with max bound)
 * - Add to cart via centralised TheColor.Cart API
 * - The same-day dispatch countdown and the live free-delivery line
 *
 * Also defines TheColor.Dispatch (below), the one dispatch-day helper.
 */

/* ==========================================================================
   Dispatch-day maths for the PDP countdown. cart.js (B06) defines the shared
   TheColor.Dispatch with the same inputs and the same status() shape
   ({ paused, sameDay, minsLeft, today, next }); the PDP uses that one when it
   is there, and this copy otherwise (it then becomes TheColor.Dispatch).
   Both read the same inputs, so the drawer and the product page agree.
   - Cut-off: window.theme.cutoffHour (number of hours, or "14:00"/"2pm"),
     else window.theme.dispatchCutoff, else the caller's fallback string
     (Theme settings → Delivery → "Same-day dispatch cut-off"), else 14:00.
   - Closed: Saturdays, Sundays, England & Wales bank holidays (Royal Mail
     doesn't collect) and window.theme.closedDates ['YYYY-MM-DD', …].
   - Holiday pause: window.theme.isDispatchPaused(iso) (layout/theme.liquid).
   All dates are Europe/London calendar dates.
   ========================================================================== */
(function () {
  'use strict';
  var TC = window.TheColor = window.TheColor || {};

  /* England & Wales bank holidays (gov.uk). Extend each year. */
  var BANK_HOLIDAYS = [
    '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31',
    '2027-08-30', '2027-12-27', '2027-12-28',
    '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-29',
    '2028-08-28', '2028-12-25', '2028-12-26'
  ];
  var DAY = 86400000;

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function isoToUtc(iso) {
    var p = String(iso).split('-');
    return Date.UTC(+p[0], +p[1] - 1, +p[2]);
  }
  function utcToIso(t) {
    var d = new Date(t);
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
  }
  function dow(iso) { return new Date(isoToUtc(iso)).getUTCDay(); }

  /* "2pm", "12pm", "11.30am", "14:00", "14", 14 → minutes after midnight */
  function parseCutoff(v) {
    if (typeof v === 'number' && isFinite(v)) {
      return v >= 0 && v <= 24 ? Math.round(v * 60) : null;
    }
    var m = String(v == null ? '' : v).trim().toLowerCase().match(/^(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?$/);
    if (!m) return null;
    var h = +m[1];
    var mi = m[2] ? +m[2] : 0;
    if (m[3] === 'pm' && h < 12) h += 12;
    if (m[3] === 'am' && h === 12) h = 0;
    if (h > 23 || mi > 59) return null;
    return h * 60 + mi;
  }

  function cutoffMinutes(fallback) {
    var t = window.theme || {};
    var c = parseCutoff(t.cutoffHour);
    if (c == null) c = parseCutoff(t.dispatchCutoff);
    if (c == null) c = parseCutoff(fallback);
    if (c == null) {
      var el = document.querySelector('[data-dispatch-cutoff]');
      if (el) c = parseCutoff(el.getAttribute('data-dispatch-cutoff'));
    }
    return c == null ? 14 * 60 : c;
  }

  function closedDates() {
    var t = window.theme || {};
    var list = t.closedDates;
    if (typeof list === 'string') list = list.split(/[\s,]+/);
    if (!Array.isArray(list)) list = [];
    return BANK_HOLIDAYS.concat(list.filter(function (s) { return /^\d{4}-\d{2}-\d{2}$/.test(String(s)); }));
  }

  function isPaused(iso) {
    var t = window.theme || {};
    return !!(t.isDispatchPaused && t.isDispatchPaused(iso));
  }

  /* A day the post goes out: Mon–Fri and not a bank holiday / closed date
     (the holiday pause is reported separately, see status()). */
  function isWorkingDay(iso) {
    var d = dow(iso);
    return d >= 1 && d <= 5 && closedDates().indexOf(iso) === -1;
  }

  function nextWorkingDay(iso) {
    var t = isoToUtc(iso);
    for (var k = 1; k <= 31; k++) {
      var s = utcToIso(t + k * DAY);
      if (isWorkingDay(s)) return s;
    }
    return '';
  }

  /* Europe/London calendar date and minutes for a Date (default: now). */
  function londonNow(date) {
    var parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    }).formatToParts(date || new Date()).forEach(function (p) { parts[p.type] = p.value; });
    var h = parseInt(parts.hour, 10) % 24;
    return { iso: parts.year + '-' + parts.month + '-' + parts.day, minutes: h * 60 + parseInt(parts.minute, 10) };
  }

  /* "tomorrow", "Monday" (within the coming weekend), else "Tue 29 Dec". */
  function dayLabel(fromIso, toIso) {
    var diff = Math.round((isoToUtc(toIso) - isoToUtc(fromIso)) / DAY);
    if (dow(toIso) === 1 && diff <= 3) return 'Monday';
    if (diff === 1) return 'tomorrow';
    var d = new Date(isoToUtc(toIso));
    var W = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return W[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + M[d.getUTCMonth()];
  }

  /* status(date?) → { paused, sameDay, minsLeft, today, next } — the shape
     cart.js uses — with { cutoff } accepted as a fallback string. */
  function status(arg) {
    var opts = arg instanceof Date ? { date: arg } : (arg || {});
    var n = londonNow(opts.date);
    var cut = cutoffMinutes(opts.cutoff);
    var beforeCut = isWorkingDay(n.iso) && n.minutes < cut;
    var next = nextWorkingDay(n.iso);
    var paused = isPaused(n.iso) || (!beforeCut && !!next && isPaused(next));
    return {
      paused: paused,
      sameDay: beforeCut && !paused,
      minsLeft: beforeCut ? cut - n.minutes : 0,
      today: n.iso,
      next: beforeCut ? n.iso : next,
      cutoffMinutes: cut
    };
  }

  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

  var local = {
    parseCutoff: parseCutoff,
    cutoffMinutes: cutoffMinutes,
    closedDates: closedDates,
    isWorkingDay: isWorkingDay,
    nextWorkingDay: nextWorkingDay,
    isPaused: isPaused,
    londonNow: londonNow,
    dayLabel: dayLabel,
    status: status
  };

  /* The PDP line, same wording as always ('' = hide: holiday pause).
     Uses the shared TheColor.Dispatch status when it speaks the same shape,
     else this copy; "next dispatch" names the real day ("tomorrow",
     "Monday", or "Tue 29 Dec" across bank holidays). */
  function pdpMessage(fallbackCutoff) {
    var s = null;
    var ext = TC.Dispatch;
    if (ext && ext !== local && typeof ext.status === 'function') {
      try {
        var r = ext.status();
        if (r && typeof r.sameDay === 'boolean' && typeof r.paused === 'boolean') s = r;
      } catch (e) { s = null; }
    }
    if (!s) s = status({ cutoff: fallbackCutoff });
    if (s.paused) return '';
    if (s.sameDay && s.minsLeft > 0) {
      if (s.minsLeft < 60) return 'Order in ' + plural(s.minsLeft, 'min', 'min') + ' for same-day dispatch';
      var h = Math.floor(s.minsLeft / 60);
      var m = s.minsLeft % 60;
      return 'Order in ' + plural(h, 'hr', 'hrs') + (m ? ' ' + plural(m, 'min', 'min') : '') + ' for same-day dispatch';
    }
    var today = s.today || londonNow().iso;
    var next = s.next && s.next !== today ? s.next : nextWorkingDay(today);
    return next ? 'Order now — next dispatch ' + dayLabel(today, next) : '';
  }

  if (!TC.Dispatch) TC.Dispatch = local;
  TC.pdpDispatchMessage = pdpMessage;
})();

(function () {
  'use strict';

  const MAX_QUANTITY = 99;
  const TC = window.TheColor = window.TheColor || {};

  /**
   * Build a Shopify CDN URL with a specific width parameter.
   * Handles both legacy _600x suffix URLs and modern ?width= URLs.
   */
  function getSizedImageUrl(src, width) {
    if (!src) return '';
    // Strip legacy _NNNx or _NNNxNNN size suffixes
    var url = src.replace(/_\d+x(\d+)?(?=\.|$)/g, '');
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', width);
      return parsed.pathname + parsed.search;
    } catch (e) {
      url = url.replace(/[?&]width=\d+/g, '');
      var sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + 'width=' + width;
    }
  }

  /*
   * Size tokens (in ml) named in a piece of text: "500ml", "500 ml",
   * "1 Litre", "1L", "Gallon (3.78L)". A unit is required, so digits from
   * upload hashes ("…f75c…") or PM item codes ("XBFS-315") never count.
   */
  var SIZE_TOKEN_RE = /(\d+(?:[.,]\d+)?)\s*(ml|millilitres?|l|litres?|liters?)(?![a-z])/gi;
  function sizesInText(text) {
    var out = [];
    if (!text) return out;
    var t = String(text).toLowerCase();
    if (t.indexOf('gallon') !== -1) out.push(3780);
    var m;
    SIZE_TOKEN_RE.lastIndex = 0;
    while ((m = SIZE_TOKEN_RE.exec(t)) !== null) {
      // "1,000 ml" is a thousands separator; "3,78 l" a decimal comma.
      var raw = m[1];
      var n = /^\d{1,3},\d{3}$/.test(raw) ? parseFloat(raw.replace(',', '')) : parseFloat(raw.replace(',', '.'));
      var ml = Math.round(m[2].charAt(0) === 'm' ? n : n * 1000);
      if (ml > 0 && out.indexOf(ml) === -1) out.push(ml);
    }
    return out;
  }

  // File part of an image URL, without query string or legacy _600x suffix —
  // the same across cdn.shopify.com, /cdn/shop and resized URLs.
  function imageFileKey(src) {
    if (!src) return '';
    var file = String(src).split('?')[0].split('#')[0].split('/').pop() || '';
    return file.replace(/_\d+x(\d+)?(?=\.[a-z0-9]+$)/i, '').toLowerCase();
  }

  // Sizes an image depicts, from its alt text and file name. A file named
  // only with the legacy PM "999" code (e.g. LMMS-999_1.jpg) is a 1 Litre.
  function imageSizes(alt, src) {
    var file = imageFileKey(src);
    var sizes = sizesInText(alt);
    sizesInText(file.replace(/[_-]+/g, ' ')).forEach(function (ml) {
      if (sizes.indexOf(ml) === -1) sizes.push(ml);
    });
    if (!sizes.length && /(^|[^0-9])999([^0-9]|$)/.test(file)) sizes.push(1000);
    return sizes;
  }

  // One "stem" per photographed item: the file key without its extension,
  // Shopify's duplicate-upload UUID suffix or a trailing 1–2 digit photo
  // number. LMMS-999_1 … LMMS-999_8 share the stem "lmms-999", while
  // TeaTree-Special-TTM-999_01 and TeaTree-Special-TTSC-999_01 are two stems
  // (two different products). PM's 3-digit size codes (-999, -300) are kept.
  function imageFileStem(src) {
    return imageFileKey(src)
      .replace(/\.[a-z0-9]+$/, '')
      .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, '')
      .replace(/[_-]\d{1,2}$/, '');
  }

  // Resolve a variant's image. An admin-assigned variant.featured_image
  // always wins. Otherwise match productData.media (which carries alt, src
  // and id) on a STRICT size key: the image must name exactly one size and
  // it must be this variant's size. Images that name no size, or several,
  // never match, so a size is never shown another size's bottle; with no
  // match the gallery stays where it is. productData.images (plain URL
  // strings in `product | json`) is only the fallback when media is absent.
  //
  // Two guards stop an inferred match from showing a DIFFERENT PRODUCT (the
  // Tea Tree Special Conditioner carries a dumped asset library in which the
  // first "999" file is the Hair and Body Moisturiser litre):
  //  - the match must be unique: if the target size is named by more than
  //    one file stem anywhere in the product's media, it is ambiguous and
  //    nothing is inferred;
  //  - when `galleryKeys` (file keys of the rendered gallery thumbnails) is
  //    passed, the match must be one of them, so an off-gallery upload is
  //    never swapped in.
  // Returns a media-shaped object: { id, src, alt, position }.
  function findVariantImage(variant, productData, galleryKeys) {
    if (!variant) return null;
    if (variant.featured_image) return variant.featured_image;
    if (!productData) return null;

    var target = 0;
    var opts = variant.options || [variant.option1, variant.option2, variant.option3];
    for (var oi = 0; oi < opts.length && !target; oi++) {
      var found = sizesInText(opts[oi]);
      if (found.length === 1) target = found[0];
    }
    if (!target) return null;

    var candidates = [];
    if (Array.isArray(productData.media) && productData.media.length) {
      productData.media.forEach(function (m) {
        if (!m || (m.media_type && m.media_type !== 'image')) return;
        var src = m.src || (m.preview_image && m.preview_image.src);
        if (src) candidates.push({ id: m.id, src: src, alt: m.alt || '', position: m.position });
      });
    } else if (Array.isArray(productData.images)) {
      productData.images.forEach(function (img, i) {
        if (!img) return;
        if (typeof img === 'string') candidates.push({ id: null, src: img, alt: '', position: i + 1 });
        else if (img.src) candidates.push({ id: img.id, src: img.src, alt: img.alt || '', position: img.position || i + 1 });
      });
    }

    var matches = [];
    var stems = [];
    for (var ci = 0; ci < candidates.length; ci++) {
      var sizes = imageSizes(candidates[ci].alt, candidates[ci].src);
      if (sizes.length === 1 && sizes[0] === target) {
        matches.push(candidates[ci]);
        var stem = imageFileStem(candidates[ci].src);
        if (stems.indexOf(stem) === -1) stems.push(stem);
      }
    }
    if (stems.length !== 1) return null;
    if (!Array.isArray(galleryKeys)) return matches[0];
    for (var mi = 0; mi < matches.length; mi++) {
      if (galleryKeys.indexOf(imageFileKey(matches[mi].src)) !== -1) return matches[mi];
    }
    return null;
  }

  const section = document.querySelector('[data-section-type="product-main"]');
  if (!section) return;

  // Parse product data
  const productJsonEl = section.querySelector('[data-product-json]');
  if (!productJsonEl) return;

  let product;
  try {
    product = JSON.parse(productJsonEl.textContent);
  } catch (e) {
    // Silent — bail without disrupting the page
    return;
  }

  // Per-variant size facts from the Liquid: { id: { q: ml, u, p100, pu } }
  let variantMeta = {};
  try {
    const metaEl = section.querySelector('[data-variant-meta]');
    if (metaEl) variantMeta = JSON.parse(metaEl.textContent) || {};
  } catch (e) { variantMeta = {}; }

  // --- Enhanced E-Commerce: view_item event for Google Ads ---
  // Wrapped + re-firing on tc:consent-granted so we don't lose the signal
  // for visitors who land on a PDP, then accept consent a few seconds
  // later. Without the re-fire, the FIRST PDP in their journey never
  // reports view_item, which is the most important signal for Smart Bidding.
  // Resolve the variant the shopper is ACTUALLY looking at, not just the
  // first available one: a ?variant= deep link (ad clicks land here) and the
  // SSR'd hidden variant-id input (which carries the landing-size choice)
  // both beat the first-available fallback.
  const currentVariant = (function () {
    if (!product.variants || !product.variants.length) return null;
    var urlId = null;
    try {
      urlId = new URLSearchParams(window.location.search).get('variant');
    } catch (e) { /* ignore — fall through to the SSR input */ }
    var ssrInput = section.querySelector('[data-product-variant-id]');
    /* Try each candidate until one MATCHES: a stale ?variant= id (deleted and
       recreated variants happen) must fall through to the SSR input — which
       carries the landing size — not straight to first-available. */
    var candidates = [urlId, ssrInput && ssrInput.value];
    for (var ci = 0; ci < candidates.length; ci++) {
      if (!candidates[ci]) continue;
      var match = product.variants.find(function (v) { return String(v.id) === String(candidates[ci]); });
      if (match) return match;
    }
    return product.variants.find(function (v) { return v.available; }) || product.variants[0];
  })();
  let viewItemFired = false;
  function fireViewItemEvent() {
    if (viewItemFired) return;
    if (typeof gtag !== 'function' || !currentVariant || !TC.utils || !TC.utils.hasConsent()) return;
    viewItemFired = true;
    gtag('event', 'view_item', {
      currency: 'GBP',
      value: currentVariant.price / 100,
      items: [{
        /* Feed-aligned id: the Google & YouTube channel submits feed items as
           shopify_ZZ_{product}_{variant} (observed from its own remarketing
           events) — matching it lets GA4/Ads join items to the Shopping feed. */
        item_id: 'shopify_ZZ_' + product.id + '_' + currentVariant.id,
        item_name: product.title,
        item_brand: product.vendor,
        item_category: product.type || undefined,
        item_variant: currentVariant.title !== 'Default Title' ? currentVariant.title : undefined,
        google_business_vertical: 'retail',
        price: currentVariant.price / 100,
        quantity: 1
      }]
    });
  }
  fireViewItemEvent();
  // If consent wasn't granted at page load, fire once it is granted later.
  document.addEventListener('tc:consent-granted', fireViewItemEvent, { once: true });

  // Elements
  const form = section.querySelector('[data-product-form]');
  const variantIdInput = section.querySelector('[data-product-variant-id]');
  const priceEl = section.querySelector('[data-product-price]');
  const comparePriceEl = section.querySelector('[data-product-compare-price]');
  const compareAmountEl = section.querySelector('[data-product-compare-amount]');
  const saveBadgeEl = section.querySelector('[data-product-save-badge]');
  const addBtn = section.querySelector('[data-product-add-btn]');
  const mainImage = section.querySelector('[data-product-featured-image]');
  const thumbnails = section.querySelectorAll('[data-product-thumbnail]');
  const optionSelects = section.querySelectorAll('[data-product-option-select]');
  const qtyInput = section.querySelector('[data-product-quantity]');
  const qtyMinus = section.querySelector('[data-product-qty-minus]');
  const qtyPlus = section.querySelector('[data-product-qty-plus]');
  const titleEl = section.querySelector('[data-pdp-title]');
  const outOfStockEl = section.querySelector('[data-out-of-stock]');
  const outOfStockText = section.querySelector('[data-out-of-stock-text]');
  const oosAltEl = section.querySelector('[data-oos-alt]');
  const oosAltText = section.querySelector('[data-oos-alt-text]');
  const oosSwitch = section.querySelector('[data-oos-switch]');
  const notifyFormEl = section.querySelector('[data-notify-form]');
  const dynamicCheckout = section.querySelector('[data-dynamic-checkout]');
  const orderByEl = section.querySelector('[data-order-by]');
  const orderByText = section.querySelector('[data-order-by-text]');
  const deliveryEl = section.querySelector('[data-delivery-line]');
  const subsNoteEl = section.querySelector('[data-subs-note]');
  const subsPctEl = section.querySelector('[data-subs-pct]');

  // File keys of the images the gallery actually renders (thumbnails are
  // capped at 20; a single-image product renders only the main photo). An
  // inferred size match must be one of these — see findVariantImage.
  const galleryKeys = (function () {
    var keys = [];
    thumbnails.forEach(function (t) {
      var k = imageFileKey(t.dataset.imageUrl);
      if (k) keys.push(k);
    });
    if (!keys.length && mainImage) {
      var mk = imageFileKey(mainImage.getAttribute('src'));
      if (mk) keys.push(mk);
    }
    return keys;
  })();

  // H1: the Liquid renders "Title <span class=…title-size>300 ml</span>";
  // rebuilt here from the SSR'd base title on size change (handles
  // "Gallon (3.78L)" too, which a trailing-"ml" strip could not).
  const titleBase = titleEl ? (titleEl.getAttribute('data-title-base') || '') : '';
  const titleSizePos = titleEl ? parseInt(titleEl.getAttribute('data-size-option'), 10) || 0 : 0;

  /**
   * Shared gallery state — ONE image index, synced across thumbnail clicks,
   * variant changes, mobile swipes and the zoom modal.
   *
   * NOTE: product.images in `{{ product | json }}` is an array of URL
   * STRINGS (no .src/.alt/.id), so the SSR'd thumbnail buttons — which carry
   * prebuilt url/srcset/alt/id per image, in product.images order — are the
   * source of truth for image switching.
   */
  let currentImageIndex = (function () {
    for (let i = 0; i < thumbnails.length; i++) {
      if (thumbnails[i].classList.contains('is-active')) return i;
    }
    return 0;
  })();

  // Apply src + srcset to the main <img> (and a WebP <source>, if a
  // <picture> still carries one — derived from the jpeg srcset).
  function applySrcsetToMain(src, srcset, alt) {
    if (!mainImage) return;
    mainImage.src = src;
    mainImage.srcset = srcset;
    mainImage.alt = alt;
    var pic = mainImage.closest('picture');
    if (pic) {
      var webpSource = pic.querySelector('source[type="image/webp"]');
      if (webpSource) {
        webpSource.srcset = srcset.split(',').map(function (entry) {
          var parts = entry.trim().split(/\s+/);
          var url = parts[0];
          var descriptor = parts[1] || '';
          if (url.indexOf('format=webp') === -1) {
            url += (url.indexOf('?') !== -1 ? '&' : '?') + 'format=webp';
          }
          return (url + ' ' + descriptor).trim();
        }).join(', ');
      }
    }
  }

  // Switch the gallery to product image `index` (thumbnail order = image
  // order) — updates main <img>, thumbnail highlight and the shared
  // currentImageIndex in one place.
  function setMainImage(index) {
    if (!mainImage) return;
    var thumb = thumbnails[index];
    if (!thumb) return;
    currentImageIndex = index;
    applySrcsetToMain(thumb.dataset.imageUrl, thumb.dataset.imageSrcset, thumb.dataset.imageAlt);
    thumbnails.forEach(function (t, i) {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  // Zoom
  const zoomTrigger = section.querySelector('[data-product-zoom]');
  const zoomModal = section.querySelector('[data-product-zoom-modal]');
  const zoomImage = section.querySelector('[data-product-zoom-image]');
  const zoomClose = section.querySelector('[data-product-zoom-close]');
  const zoomPrev = section.querySelector('[data-product-zoom-prev]');
  const zoomNext = section.querySelector('[data-product-zoom-next]');
  const zoomCount = section.querySelector('[data-product-zoom-count]');

  // Duo component stock — slim JSON: { shampoo: {variants:[{option1, available}]} | null, conditioner: … }
  let duoComponents = null;
  const duoJsonEl = section.querySelector('[data-duo-components]');
  if (duoJsonEl) {
    try {
      duoComponents = JSON.parse(duoJsonEl.textContent);
    } catch (e) {
      duoComponents = null;
    }
  }

  function normalizeSize(s) {
    var k = s ? String(s).toLowerCase().replace(/\s+/g, '') : '';
    // A set size that names its serum ("300 ml + 250 ml serum") matches its
    // bottles' size ("300ml"); every other title is unchanged.
    if (k.indexOf('mlserum') !== -1) k = k.split('+')[0];
    return k;
  }

  /**
   * Can this variant be bought? For bundles, a RESOLVED shampoo or
   * conditioner whose same-size single is out of stock makes it "Sold Out"
   * (matches the Liquid). An unresolved part (typo handle → null) or a part
   * without that size defers to the bundle variant's own stock — bundle
   * inventory is linked to its parts (script 61).
   */
  function isVariantAvailable(variant) {
    if (!variant) return false;
    if (!variant.available) return false;
    if (!duoComponents) return true;
    var sh = duoComponents.shampoo;
    var cd = duoComponents.conditioner;
    if (!sh || !cd || !Array.isArray(sh.variants) || !Array.isArray(cd.variants)) return true;
    var size = normalizeSize(variant.option1);
    var parts = [sh, cd];
    for (var i = 0; i < parts.length; i++) {
      var pv = parts[i].variants.find(function (v) { return normalizeSize(v.option1) === size; });
      if (pv && !pv.available) return false;
    }
    return true;
  }

  function componentSoldOut(variant) {
    return !!(duoComponents && variant && variant.available && !isVariantAvailable(variant));
  }

  /**
   * Get currently selected options
   */
  function getSelectedOptions() {
    const options = [];

    optionSelects.forEach((select) => {
      options.push(select.value);
    });

    // Radio-style inputs (size chips, colour swatches)
    const optionGroups = section.querySelectorAll('[data-product-option]');
    optionGroups.forEach((group) => {
      const checkedInput = group.querySelector('[data-product-option-input]:checked');
      if (checkedInput) {
        const index = parseInt(group.dataset.optionIndex, 10);
        options[index] = checkedInput.value;
      }
    });

    return options;
  }

  /**
   * Find variant by selected options
   */
  function findVariant(selectedOptions) {
    return product.variants.find((variant) =>
      variant.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function formatMoney(cents) {
    if (TC.utils && typeof TC.utils.formatMoney === 'function') return TC.utils.formatMoney(cents);
    return '£' + (cents / 100).toFixed(2);
  }

  // "£30.00" → "£30" for whole-pound thresholds
  function formatPounds(cents) {
    return cents % 100 === 0 ? '£' + (cents / 100) : formatMoney(cents);
  }

  function sizeOf(variant) {
    if (!variant || !titleSizePos) return '';
    return variant['option' + titleSizePos] || '';
  }

  // Show only the [attr="<variant id>"] span inside each wrapper.
  function toggleFor(selector, attr, id) {
    var any = false;
    section.querySelectorAll(selector).forEach(function (el) {
      var match = el.getAttribute(attr) === id;
      el.hidden = !match;
      if (match) any = true;
    });
    return any;
  }

  /**
   * The nearest in-stock size to a sold-out pick (same other options, same
   * unit), with its price per 100 — "The 1000 ml is in stock — £4.90/100 ml".
   */
  function nearestInStock(variant) {
    var me = variantMeta[String(variant.id)];
    if (!me || !(me.q > 0)) return null;
    var best = null;
    var bestScore = Infinity;
    product.variants.forEach(function (v) {
      if (v.id === variant.id || !isVariantAvailable(v)) return;
      var m = variantMeta[String(v.id)];
      if (!m || !(m.q > 0) || m.u !== me.u) return;
      for (var i = 0; i < v.options.length; i++) {
        if (i + 1 !== titleSizePos && v.options[i] !== variant.options[i]) return;
      }
      // Distance on a log scale; on a tie prefer the larger size.
      var score = Math.abs(Math.log(m.q / me.q)) - (m.q > me.q ? 1e-6 : 0);
      if (score < bestScore) { bestScore = score; best = v; }
    });
    return best;
  }

  function updateOosAlt(variant, available) {
    if (!oosAltEl) return;
    var alt = (!available && variant) ? nearestInStock(variant) : null;
    if (!alt) {
      oosAltEl.hidden = true;
      return;
    }
    var label = sizeOf(alt) || alt.title;
    var m = variantMeta[String(alt.id)] || {};
    var unit = m.p100 > 0 ? ' — ' + formatMoney(m.p100) + '/100' + (m.pu || 'ml') : '';
    if (oosAltText) oosAltText.textContent = 'The ' + label + ' is in stock' + unit + '.';
    if (oosSwitch) {
      oosSwitch.textContent = 'Switch to ' + label;
      oosSwitch.setAttribute('data-variant', String(alt.id));
    }
    oosAltEl.hidden = false;
  }

  if (oosSwitch) {
    oosSwitch.addEventListener('click', function () {
      var id = oosSwitch.getAttribute('data-variant');
      var target = product.variants.find(function (v) { return String(v.id) === id; });
      if (!target) return;
      selectVariantInputs(target);
    });
  }

  // Check the radio inputs (or selects) for a variant and run the update.
  function selectVariantInputs(variant) {
    section.querySelectorAll('[data-product-option]').forEach(function (group) {
      var idx = parseInt(group.dataset.optionIndex, 10);
      var value = variant.options[idx];
      group.querySelectorAll('[data-product-option-input]').forEach(function (input) {
        if (input.value === value) {
          input.checked = true;
          if (input.closest('.tc-product-main__chip')) {
            setTimeout(function () { input.focus({ preventScroll: true }); }, 0);
          }
        }
      });
    });
    optionSelects.forEach(function (select, idx) {
      if (variant.options[idx] != null) select.value = variant.options[idx];
    });
    onOptionChange();
  }

  // Chip highlight without :has() (iOS 15 and older engines).
  function syncChipSelection() {
    section.querySelectorAll('.tc-product-main__chip').forEach(function (chip) {
      var input = chip.querySelector('[data-product-option-input]');
      chip.classList.toggle('is-selected', !!(input && input.checked));
    });
  }

  section.addEventListener('focusin', function (e) {
    var chip = e.target.closest && e.target.closest('.tc-product-main__chip');
    if (!chip) return;
    var visible = true;
    try { visible = e.target.matches(':focus-visible'); } catch (err) { /* old engine: always show */ }
    if (visible) chip.classList.add('is-focus');
  });
  section.addEventListener('focusout', function (e) {
    var chip = e.target.closest && e.target.closest('.tc-product-main__chip');
    if (chip) chip.classList.remove('is-focus');
  });

  let liveVariant = currentVariant;
  let liveAvailable = isVariantAvailable(currentVariant);

  /**
   * Update product info for selected variant
   */
  function updateVariant(variant) {
    if (!variant) {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = 'Unavailable';
      }
      return;
    }

    var isAvailable = isVariantAvailable(variant);
    var id = String(variant.id);
    var size = sizeOf(variant);
    liveVariant = variant;
    liveAvailable = isAvailable;

    // Hidden input
    if (variantIdInput) variantIdInput.value = variant.id;

    // Everything below is presentation. variant:change (the sticky bar, set
    // sizes and photos, the pairs strip and the duo CTA listen for it) is
    // sent from `finally`, so one failing helper can never swallow it — a
    // stale helper once threw here and every listener stopped following the
    // size. The error itself still reaches the console.
    try {
      // H1: base title + lighter size span ("Standard" / "Default Title"
      // are not sizes and never show; SEO masterclass, 18 Oct 2026)
      if (titleEl && titleBase) {
        // Design polish round 1, item 7 (22 Oct 2026): keep the brand eyebrow
        // span the Liquid rendered (data-title-brand) when rebuilding the H1.
        var brand = titleEl.getAttribute('data-title-brand') || '';
        titleEl.textContent = '';
        if (brand && titleBase.toLowerCase().indexOf(brand.toLowerCase()) === 0 && titleBase.length > brand.length) {
          var bSpan = document.createElement('span');
          bSpan.className = 'tc-product-main__title-brand';
          bSpan.textContent = titleBase.slice(0, brand.length);
          var rSpan = document.createElement('span');
          rSpan.className = 'tc-product-main__title-rest';
          rSpan.textContent = titleBase.slice(brand.length).trim();
          titleEl.appendChild(bSpan);
          titleEl.appendChild(document.createTextNode(' '));
          titleEl.appendChild(rSpan);
        } else {
          titleEl.textContent = titleBase;
        }
        if (size && !/^\s*(standard|default title)\s*$/i.test(size)) {
          var span = document.createElement('span');
          span.className = 'tc-product-main__title-size';
          span.textContent = size;
          titleEl.appendChild(document.createTextNode(' '));
          titleEl.appendChild(span);
        }
      }

      syncChipSelection();

      // Price, "RRP £x" and "Save £x on RRP" (the pill only on a buyable size)
      // 13 Oct 2026: a strike only on bundles/duos (compare-at = bought separately); never on a single.
      var priceWrapEl = section.querySelector('[data-product-price-wrapper]');
      var isSet = !priceWrapEl || priceWrapEl.getAttribute('data-is-set') !== 'false';
      // Sale on singles (22 Oct 2026): a product tagged "sale" shows "Was £x" + "N% off" on the sizes that have a compare-at.
      var isSaleProduct = !!priceWrapEl && priceWrapEl.getAttribute('data-sale-product') === 'true';
      // RRP on singles (24 Oct 2026): a product tagged "show-sale-price" shows "RRP £x" + "Save £y on RRP" on the sizes that have a compare-at.
      var isRrpProduct = !!priceWrapEl && priceWrapEl.getAttribute('data-rrp-product') === 'true';
      var hasRrp = (isSet || isSaleProduct || isRrpProduct) && !!(variant.compare_at_price && variant.compare_at_price > variant.price);
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
        priceEl.className = hasRrp ? 'tc-product-main__price-sale' : 'tc-product-main__price-regular';
      }
      if (comparePriceEl) {
        if (compareAmountEl) compareAmountEl.textContent = hasRrp ? formatMoney(variant.compare_at_price) : '';
        comparePriceEl.hidden = !hasRrp;
      }
      if (saveBadgeEl) {
        saveBadgeEl.textContent = !hasRrp ? '' : (isSaleProduct && !isSet)
          ? Math.floor((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) + '% off'
          : (isRrpProduct && !isSet)
          ? 'Save ' + formatMoney(variant.compare_at_price - variant.price) + ' on RRP'
          : 'Save ' + formatMoney(variant.compare_at_price - variant.price);
        saveBadgeEl.hidden = !(hasRrp && isAvailable);
      }

      // Per-size SSR'd spans
      toggleFor('[data-unit-price-for]', 'data-unit-price-for', id);
      var sepWrap = section.querySelector('[data-sep-save]');
      if (sepWrap) {
        var hasSep = toggleFor('[data-sep-save-for]', 'data-sep-save-for', id);
        sepWrap.hidden = hasRrp || !(hasSep && isAvailable);
      }
      var setLine = section.querySelector('[data-set-line]');
      if (setLine) {
        var hasSet = toggleFor('[data-set-line-for]', 'data-set-line-for', id);
        setLine.hidden = !hasSet;
      }

      // Add to Cart (for bundles, also the component stock)
      if (addBtn) {
        addBtn.classList.remove('is-pending');
        addBtn.disabled = !isAvailable;
        addBtn.textContent = tcAtcLabel(isAvailable);
      }

      // Sold-out note (+ nearest in-stock size), notify form, Buy it now
      if (outOfStockEl) {
        if (outOfStockText) {
          // "this duo" only on a true two-product duo; sets say "this set".
          outOfStockText.textContent = componentSoldOut(variant)
            ? (outOfStockText.getAttribute('data-part-oos-msg') || 'One of the products in this set is currently out of stock')
            : (product.variants.length > 1 && size ? 'The ' + size + ' is currently out of stock' : 'This item is currently out of stock');
        }
        outOfStockEl.hidden = isAvailable;
      }
      updateOosAlt(variant, isAvailable);
      if (notifyFormEl) notifyFormEl.hidden = isAvailable;
      if (dynamicCheckout) dynamicCheckout.hidden = !isAvailable;

      // Low stock (figures SSR'd per variant; the client JSON has no counts)
      var lowStockEl = section.querySelector('[data-low-stock]');
      if (lowStockEl) {
        var anyLow = false;
        lowStockEl.querySelectorAll('[data-low-stock-for]').forEach(function (el) {
          var match = isAvailable && el.getAttribute('data-low-stock-for') === id;
          el.hidden = !match;
          if (match) anyLow = true;
        });
        lowStockEl.hidden = !anyLow;
      }

      // F&S multipack card for this bottle size (photo = this size)
      section.querySelectorAll('[data-stockup-pack]').forEach(function (card) {
        var key = ',' + id + ',';
        card.hidden = (card.getAttribute('data-for-variants') || '').indexOf(key) === -1;
        card.setAttribute('data-state', (card.getAttribute('data-better-for') || '').indexOf(key) !== -1 ? 'better' : 'neutral');
        card.querySelectorAll('[data-stockup-cmp-for]').forEach(function (el) {
          el.hidden = el.getAttribute('data-stockup-cmp-for') !== id;
        });
      });

      updateOrderBy();
      updateDelivery();

      // Featured image — falls back to alt/filename match when variant.featured_image is unset
      var resolvedImage = findVariantImage(variant, product, galleryKeys);
      if (resolvedImage && mainImage) {
        // Route through the shared gallery switcher so currentImageIndex stays
        // in sync with what the shopper sees (match thumbnail by image id, or
        // by file name for media-shaped matches, whose id is the media id).
        var thumbIdx = -1;
        var resolvedKey = imageFileKey(resolvedImage.src);
        for (var ti = 0; ti < thumbnails.length; ti++) {
          if (resolvedImage.id != null && thumbnails[ti].dataset.imageId === String(resolvedImage.id)) {
            thumbIdx = ti;
            break;
          }
        }
        if (thumbIdx === -1 && resolvedKey) {
          for (var tk = 0; tk < thumbnails.length; tk++) {
            if (imageFileKey(thumbnails[tk].dataset.imageUrl) === resolvedKey) {
              thumbIdx = tk;
              break;
            }
          }
        }
        if (thumbIdx !== -1) {
          setMainImage(thumbIdx);
        } else if (resolvedImage.src) {
          // No matching thumbnail — only an admin-assigned variant image past
          // the 20-thumbnail cap gets here — update in place
          const baseSrc = resolvedImage.src;
          const src600 = getSizedImageUrl(baseSrc, 600);
          const src900 = getSizedImageUrl(baseSrc, 900);
          const src1200 = getSizedImageUrl(baseSrc, 1200);
          applySrcsetToMain(
            src900,
            src600 + ' 600w, ' + src900 + ' 900w, ' + src1200 + ' 1200w',
            resolvedImage.alt || product.title
          );
        }
      }

      // Update URL without reload
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    } finally {
      // variant:change for the sticky bar, pairs strip, duo CTA and others
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant,
          available: isAvailable,
          duoAvailable: duoComponents ? isAvailable : null,
          size: size
        }
      }));
    }
  }

  /**
   * Handle option changes — batched in a microtask (Promise.resolve) so
   * rapid clicks don't run the gallery + price work twice. Not rAF: rAF is
   * throttled in background tabs and chip clicks then appeared to "do
   * nothing" until a refresh.
   */
  let optionPending = false;
  function onOptionChange() {
    if (optionPending) return;
    optionPending = true;
    Promise.resolve().then(() => {
      optionPending = false;
      const selectedOptions = getSelectedOptions();
      const variant = findVariant(selectedOptions);
      updateVariant(variant);
    });
  }

  // Delegated listener on the section — handles every variant input
  // (selects, swatches, chips) regardless of render order or DOM rewrites.
  section.addEventListener('change', (e) => {
    const t = e.target;
    if (t && (t.matches('[data-product-option-input]') || t.matches('[data-product-option-select]'))) {
      onOptionChange();
    }
  });

  /* Subscribe & save (S1). Shopify's Subscriptions app block first shows the
     plans for product.selected_or_first_available_variant, then follows the
     size by watching this hidden input's value ATTRIBUTE (a MutationObserver;
     updateVariant's `.value =` writes the attribute, as it does on any
     hidden input). The landing size can differ from that variant (a bare-URL
     Freeze & Shine page lands on 1 L), and the block's own script only
     re-syncs on a change. So once its deferred script has run, write the
     same value again: the block then shows the plans for the size on screen
     and its selling_plan input follows that size. Harmless without the
     block, and it keeps the shopper's pick (it never unticks a radio). */
  if (variantIdInput) {
    const resyncPlans = () => {
      if (!section.querySelector('[name="selling_plan"]')) return;
      variantIdInput.setAttribute('value', variantIdInput.value);
    };
    if (document.readyState === 'complete') {
      setTimeout(resyncPlans, 0);
    } else {
      document.addEventListener('DOMContentLoaded', resyncPlans, { once: true });
      window.addEventListener('load', resyncPlans, { once: true });
    }
  }

  /**
   * Same-day dispatch line — TheColor.Dispatch (top of this file): the
   * cut-off from Theme settings, weekends, bank holidays, closed dates and
   * the holiday pause. Wording unchanged, and never hidden by stock (owner
   * rule, Oct 2026): only the holiday pause hides the countdown, as before.
   * Updates every minute.
   */
  function updateOrderBy() {
    if (!orderByEl || !orderByText || typeof TC.pdpDispatchMessage !== 'function') return;
    var msg = '';
    try {
      msg = TC.pdpDispatchMessage(orderByEl.getAttribute('data-dispatch-cutoff'));
    } catch (e) {
      // A helper error must never stop variant:change below it; keep the SSR line.
      return;
    }
    if (!msg) {
      orderByEl.hidden = true;
      return;
    }
    orderByText.textContent = msg;
    orderByEl.hidden = false;
  }
  updateOrderBy();
  setInterval(updateOrderBy, 60 * 1000);

  /**
   * Free-delivery line, live on the shopper's own basket: "Your order already
   * ships free", "Ships free with this", or "£x to go with this". The basket
   * comes from cart.js's cart:updated (it fires on its first render), else
   * the recs engine's cached cart. The threshold is the theme setting. The
   * SSR text ("Free UK delivery on orders of £30 or more") stays until a
   * basket is known.
   *
   * 18 Oct 2026: free delivery is worked out AFTER codes at checkout (£0 at
   * £30.00 or more). With a 15% first-order code on the basket (FIRST15 or
   * WELCOME15, or waiting in the discount_code cookie) the basket and this
   * item are judged after the 15%, so the line never promises free delivery
   * that checkout won't give.
   *
   * Google seamless (18 Oct 2026, night):
   * - The code state comes from the BASKET only (the tab's tc_g15 flag
   *   outlived a code the shopper removed). FIRST15 on the website (19 Oct
   *   2026): neither code has a minimum. FIRST15 counts first; WELCOME15
   *   (honoured for baskets that already carry it) counts only while it
   *   works.
   * - The 15% is worked out as Shopify does (15% of the pence, part-penny
   *   dropped: £16.95 → £14.41, where the old 85% floor gave £14.40), and the
   *   "to go" figure is in shelf prices, the same figure the basket drawer
   *   gives ("add £4.35 more"), because that is what the shopper adds.
   *   Another code that applies (e.g. from our emails) is counted the same
   *   way, at the basket's own rate: "… after your code — add £x more".
   *   19 Oct 2026 (gap fix): "the basket's own rate" is now the code's own
   *   rate on one-off items (cart.js CartRenderer.freeKeep), as the basket
   *   bar quotes it; the basket's average let Subscribe & save lines dilute
   *   it, so the figure fell short on mixed baskets.
   * - Code holders see what this item really costs them, by the price, on
   *   the One-off card and in the sticky bar: "£14.41 with your 15% off ·
   *   applied at checkout" (size and quantity followed). Never for Subscribe
   *   & save (the code doesn't cover it) or a size that can't be bought.
   */
  const FIRST_CODES = ['FIRST15', 'WELCOME15'];
  function isFirstCode(code) {
    return FIRST_CODES.indexOf(String(code || '').trim().toUpperCase()) > -1;
  }
  function firstCodeState(cart) {
    let applying = false;
    let other = false;
    let code = '';
    /* WELCOME15 that doesn't apply to one-off items isn't working (e.g. switched off): not a 15%. */
    const oneOffIn = ((cart && cart.items) || []).some(function (i) { return i && !i.selling_plan_allocation && !i.gift_card; });
    const onCart = [];
    try {
      ((cart && cart.discount_codes) || []).forEach(function (d) {
        if (d && d.code) onCart.push(String(d.code).trim().toUpperCase());
        if (d && isFirstCode(d.code)) {
          const k = String(d.code).trim().toUpperCase();
          if (k === 'WELCOME15' && !d.applicable && oneOffIn) return;
          if (!code || k === 'FIRST15') code = k;
          if (d.applicable) applying = true;
        } else if (d && d.applicable) other = true;
      });
      if (!code) {
        /* a code still waiting in the cookie (not one the basket already holds) */
        const m = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
        const k = m ? decodeURIComponent(m[1]).trim().toUpperCase() : '';
        if (k && isFirstCode(k) && onCart.indexOf(k) === -1) code = k;
      }
    } catch (e) { /* cookie blocked: judge on the cart alone */ }
    /* other: a different code (e.g. from our emails) is the one Shopify is using */
    return { on: !!code, applying: applying, code: code, other: other && !applying };
  }
  /* Shopify takes 15% off and drops the part-penny (tested 18 Oct: 15% of £50.85 = £7.62). */
  function firstOff(cents) { return Math.floor(cents * 15 / 100); }
  function oneOffTotal(cart) {
    let s = 0;
    ((cart && cart.items) || []).forEach(function (i) {
      if (i && !i.selling_plan_allocation && !i.gift_card) s += Number(i.original_line_price != null ? i.original_line_price : i.line_price) || 0;
    });
    return s;
  }
  function pdpQty() {
    return qtyInput ? Math.max(1, Math.min(MAX_QUANTITY, parseInt(qtyInput.value, 10) || 1)) : 1;
  }
  /* What an added one-off item keeps after the basket's codes, in parts per
     million (19 Oct 2026): the basket bar's own rule (cart.js), else the
     basket's average as before. */
  function cartKeep(cart) {
    if (TC.CartUI && typeof TC.CartUI.freeDeliveryKeep === 'function') return TC.CartUI.freeDeliveryKeep(cart);
    const o = (cart && cart.original_total_price) || 0;
    const d = (cart && cart.total_discount) || 0;
    return (o > 0 && d > 0) ? Math.max(0, Math.floor(1000000 - d * 1000000 / o)) : 1000000;
  }
  let knownCart = null;
  const deliverySsr = deliveryEl ? (deliveryEl.getAttribute('data-oneoff') || deliveryEl.textContent) : '';

  /* "£14.41 with your 15% off · applied at checkout" for code holders. */
  const firstEl = section.querySelector('[data-tc-first-price]');
  const firstCardEl = section.querySelector('[data-tc-first-card]');
  let firstSent = null;
  function updateFirstPrice() {
    let line = null;
    let card = '';
    let sticky = '';
    const code = knownCart ? firstCodeState(knownCart) : { on: false };
    if (code.on && !code.other && liveVariant && liveAvailable && !(product && product.gift_card)) {
      const qty = pdpQty();
      const lineCents = liveVariant.price * qty;
      const sub = onSubscription();
      /* No minimum (19 Oct 2026), so always the price with the 15%. */
      const each = formatMoney(liveVariant.price - firstOff(liveVariant.price));
      /* the One-off card keeps its line while Subscribe & save is picked, so the two can be compared */
      card = each + ' with your 15% off';
      if (!sub) {
        line = [qty > 1 ? qty + ' for ' + formatMoney(lineCents - firstOff(lineCents)) : each, ' with your 15% off · applied at checkout'];
        sticky = card;
      }
    }
    if (firstEl) {
      firstEl.hidden = !line;
      if (line) {
        const amt = firstEl.querySelector('[data-tc-first-amt]');
        const rest = firstEl.querySelector('[data-tc-first-rest]');
        if (amt && amt.textContent !== line[0]) amt.textContent = line[0];
        if (rest && rest.textContent !== line[1]) rest.textContent = line[1];
      }
    }
    if (firstCardEl) {
      firstCardEl.hidden = !card;
      if (card && firstCardEl.textContent !== card) firstCardEl.textContent = card;
    }
    if (firstSent !== sticky) {
      firstSent = sticky;
      document.dispatchEvent(new CustomEvent('tc:firstprice', { detail: { text: sticky } }));
    }
  }

  function updateDelivery() {
    updateFirstPrice();
    if (!deliveryEl) return;
    /* Subscribe & save picked (13 Oct 2026): every subscription delivery
       ships free (UK standard), whatever its value. */
    if (onSubscription()) {
      deliveryEl.textContent = 'Free UK delivery on every subscription delivery';
      return;
    }
    var threshold = parseInt(deliveryEl.getAttribute('data-threshold'), 10) ||
      ((window.theme && window.theme.freeShippingThreshold) || 0);
    if (!knownCart || !threshold || !liveVariant || !liveAvailable) {
      deliveryEl.textContent = deliverySsr;
      return;
    }
    var addThis = liveVariant.price * pdpQty();
    var basket = knownCart.total_price || 0;
    /* shelf total with this item, what it comes to after codes, and what
       every £1 of one-off items added keeps after the codes (keep, parts per
       million). 19 Oct 2026 (gap fix): keep is the code's own rate on one-off
       items (cart.js CartRenderer.freeKeep, the basket bar's rule), not the
       basket's average, which Subscribe & save lines (never discounted)
       diluted: "add £x more" fell short on mixed baskets. */
    var orig = (knownCart.original_total_price || 0) + addThis;
    var withThis = basket + addThis;
    var keep = 1000000;
    var label = '';
    var code = firstCodeState(knownCart);
    if (code.on && !code.other) {
      var eligible = oneOffTotal(knownCart) + addThis;
      /* the basket's own codes may already save more (Shopify uses the best one) */
      var off = Math.max(firstOff(eligible), knownCart.total_discount || 0);
      /* FIRST15 / WELCOME15 take 15% off every one-off item, applied or still waiting */
      keep = Math.min(850000, cartKeep(knownCart));
      withThis = orig - off;
      if (!code.applying && !(knownCart.total_discount > 0)) basket -= firstOff(oneOffTotal(knownCart));
      label = '15% off';
    } else if ((knownCart.total_discount || 0) > 0 && knownCart.original_total_price > 0 &&
      (knownCart.discount_codes || []).some(function (d) { return d && d.applicable; })) {
      /* another code (e.g. from our emails) takes its share of this item too
         (the part-penny counted against the shopper, so never a promise
         checkout won't keep) */
      keep = cartKeep(knownCart);
      withThis = basket + addThis - Math.ceil(addThis * (1000000 - keep) / 1000000);
      label = 'your code';
    }
    var over = formatPounds(threshold);
    if (basket >= threshold) {
      deliveryEl.textContent = 'Your order already ships free (UK)';
    } else if (withThis >= threshold) {
      deliveryEl.textContent = label
        ? 'Ships free with this, even after ' + label + ' (UK orders of ' + over + ' or more)'
        : 'Ships free with this (UK orders of ' + over + ' or more)';
    } else if (label) {
      /* In shelf prices, as the drawer says it: with 15% off, every £1 added moves the total 85p. */
      var gap = threshold - withThis;
      deliveryEl.textContent = 'Free UK delivery on orders of ' + over + ' or more after ' + label + ' — add ' + formatMoney(keep > 0 ? Math.ceil(gap * 1000000 / keep) : gap) + ' more with this';
    } else {
      deliveryEl.textContent = 'Free UK delivery on orders of ' + over + ' or more — ' + formatMoney(threshold - withThis) + ' to go with this';
    }
  }
  document.addEventListener('cart:updated', function (e) {
    if (e && e.detail && e.detail.cart) {
      knownCart = e.detail.cart;
      updateDelivery();
    }
  });

  /**
   * Subscribe & save (owner, 13 Oct 2026): 15% off and free UK delivery on
   * every subscription delivery. While a plan is picked in the Shopify
   * Subscriptions block (or the product is sold only on subscription), the
   * note under the block shows ("15% off and free UK delivery on every
   * delivery. Skip, change or cancel any time.", the percentage from the
   * plan picked) and the delivery line reads "Free UK delivery on every
   * subscription delivery". One-time purchase: the note hides and the line
   * goes back to the basket maths above. The plan is read the way Add to
   * Cart reads it (TheColor.Cart.sellingPlanFrom), else from the block's
   * hidden input.
   */
  function pickedPlanId() {
    if (TC.Cart && typeof TC.Cart.sellingPlanFrom === 'function' && form) {
      return TC.Cart.sellingPlanFrom(form, section);
    }
    const el = section.querySelector('input[name="selling_plan"]');
    const n = el ? parseInt(el.value, 10) : 0;
    return n > 0 ? n : null;
  }
  function onSubscription() {
    return !!(product && product.requires_selling_plan) || !!pickedPlanId();
  }
  function planPercent(id) {
    let pct = 0;
    ((product && product.selling_plan_groups) || []).forEach(function (g) {
      ((g && g.selling_plans) || []).forEach(function (sp) {
        if (pct || !sp || String(sp.id) !== String(id)) return;
        (sp.price_adjustments || []).forEach(function (a) {
          if (!pct && a && a.value_type === 'percentage' && Number(a.value) > 0) pct = Number(a.value);
        });
      });
    });
    return pct;
  }
  function updateSubs() {
    const sub = onSubscription();
    if (subsNoteEl) subsNoteEl.hidden = !sub;
    if (sub && subsPctEl) {
      const pct = planPercent(pickedPlanId());
      if (pct) subsPctEl.textContent = (Math.round(pct * 10) / 10) + '%';
    }
    updateDelivery();
  }
  /* The block updates its hidden input after the click and redraws for a
     new size, so read again a moment later too (as the Circle lines do). */
  function updateSubsSoon() { updateSubs(); setTimeout(updateSubs, 0); setTimeout(updateSubs, 200); }
  section.addEventListener('change', function (e) {
    const t = e.target;
    if (!t || !t.closest) return;
    if (t.name === 'selling_plan' || t.hasAttribute('data-radio-type') || t.closest('.tc-product-main__app-block')) updateSubsSoon();
  });
  document.addEventListener('variant:change', updateSubsSoon);
  if (document.readyState === 'complete') updateSubsSoon();
  else window.addEventListener('load', updateSubsSoon, { once: true });
  updateSubs();
  (function primeCart() {
    function go() {
      if (knownCart || !TC.Recs || typeof TC.Recs.getCart !== 'function') return;
      TC.Recs.getCart(1500).then(function (c) {
        if (c && !knownCart) { knownCart = c; updateDelivery(); }
      }).catch(function () {});
    }
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 4000 });
    else setTimeout(go, 2000);
  })();

  // A sold-out landing (?variant= deep link) gets its nearest-size offer.
  if (currentVariant && !liveAvailable) updateOosAlt(currentVariant, false);

  // The chip highlight (.is-selected, for engines without :has()) follows
  // what is actually checked. A browser that restores radio state on reload
  // (Firefox) may check a different size than the server rendered: the page
  // then follows that size.
  (function () {
    var restored = findVariant(getSelectedOptions());
    if (restored && currentVariant && restored.id !== currentVariant.id) onOptionChange();
    else syncChipSelection();
  })();

  /**
   * Thumbnail gallery — routes through the shared setMainImage.
   */
  thumbnails.forEach((thumb, index) => {
    thumb.addEventListener('click', () => {
      setMainImage(index);
    });
  });

  /**
   * Touch swipe for main image (mobile) — same shared setMainImage path as
   * thumbnail clicks and variant changes.
   */
  (function () {
    if (!mainImage || thumbnails.length <= 1) return;

    let startX = 0;
    let startY = 0;

    mainImage.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    mainImage.addEventListener('touchend', (e) => {
      const diff = startX - e.changedTouches[0].clientX;
      const dy = startY - e.changedTouches[0].clientY;
      if (Math.abs(diff) < 50 || Math.abs(dy) > Math.abs(diff)) return;

      if (diff > 0 && currentImageIndex < thumbnails.length - 1) {
        setMainImage(currentImageIndex + 1);
      } else if (diff < 0 && currentImageIndex > 0) {
        setMainImage(currentImageIndex - 1);
      }
    }, { passive: true });
  })();

  /**
   * Scroll lock for the zoom modal: TheColor.ScrollLock (ref-counted, from
   * theme.js) when it exists, else a documentElement + body overflow lock
   * (body alone doesn't lock: base.css sets html{overflow-x:hidden}).
   */
  const scrollLock = (function () {
    var held = false;
    var prev = null;
    return {
      lock: function () {
        if (held) return;
        held = true;
        var SL = TC.ScrollLock;
        if (SL && typeof SL.lock === 'function') { SL.lock('pdp-zoom'); return; }
        prev = [document.documentElement.style.overflow, document.body.style.overflow];
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      },
      unlock: function () {
        if (!held) return;
        held = false;
        var SL = TC.ScrollLock;
        if (SL && typeof SL.unlock === 'function') { SL.unlock('pdp-zoom'); return; }
        document.documentElement.style.overflow = prev ? prev[0] : '';
        document.body.style.overflow = prev ? prev[1] : '';
        prev = null;
      }
    };
  })();

  /**
   * Image zoom — prev/next and "n / N" when there is more than one photo;
   * ←/→ keys; focus trapped while open; the <img> gets its src on open.
   */
  if (zoomTrigger && zoomModal && zoomImage) {
    let previousFocus = null;
    let releaseZoomFocus = null;
    const total = thumbnails.length;

    function showZoomImage() {
      var src;
      var alt;
      if (total > 0 && thumbnails[currentImageIndex]) {
        src = thumbnails[currentImageIndex].dataset.imageUrl;
        alt = thumbnails[currentImageIndex].dataset.imageAlt || '';
      } else if (mainImage) {
        src = mainImage.currentSrc || mainImage.src;
        alt = mainImage.alt;
      }
      if (!src) return;
      zoomImage.src = getSizedImageUrl(src, 1600);
      zoomImage.alt = alt || product.title;
      if (zoomCount && total > 1) zoomCount.textContent = (currentImageIndex + 1) + ' / ' + total;
    }

    function step(delta) {
      if (total < 2) return;
      var next = (currentImageIndex + delta + total) % total;
      setMainImage(next);
      showZoomImage();
    }

    function openZoom() {
      if (!mainImage) return;
      previousFocus = document.activeElement;
      // The main photo may be an admin variant image past the thumbnail cap:
      // zoom that exact photo, without prev/next.
      if (total > 0 && thumbnails[currentImageIndex] &&
          imageFileKey(thumbnails[currentImageIndex].dataset.imageUrl) === imageFileKey(mainImage.currentSrc || mainImage.src)) {
        showZoomImage();
      } else {
        zoomImage.src = getSizedImageUrl(mainImage.currentSrc || mainImage.src, 1600);
        zoomImage.alt = mainImage.alt;
        if (zoomCount) zoomCount.textContent = '';
      }
      zoomModal.classList.add('is-open');
      zoomModal.setAttribute('aria-hidden', 'false');
      scrollLock.lock();
      // The modal fades in from visibility:hidden, and a hidden element can't
      // take focus. Wait two frames so it is visible, then trap focus inside
      // it and land on the close button.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!zoomModal.classList.contains('is-open')) return;
          if (typeof TC.trapFocus === 'function') {
            if (releaseZoomFocus) releaseZoomFocus();
            releaseZoomFocus = TC.trapFocus(zoomModal);
          }
          if (zoomClose) {
            zoomClose.focus();
            if (document.activeElement !== zoomClose) {
              // Still mid-transition on a slow frame: try once more after it.
              setTimeout(() => {
                if (zoomModal.classList.contains('is-open')) zoomClose.focus();
              }, 320);
            }
          }
        });
      });
    }

    function closeZoom() {
      if (!zoomModal.classList.contains('is-open')) return;
      zoomModal.classList.remove('is-open');
      zoomModal.setAttribute('aria-hidden', 'true');
      zoomImage.removeAttribute('src');
      zoomImage.alt = '';
      scrollLock.unlock();
      if (typeof releaseZoomFocus === 'function') {
        releaseZoomFocus();
        releaseZoomFocus = null;
      }
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
      previousFocus = null;
    }

    zoomTrigger.addEventListener('click', openZoom);
    if (zoomClose) zoomClose.addEventListener('click', closeZoom);
    if (zoomPrev) zoomPrev.addEventListener('click', (e) => { e.stopPropagation(); step(-1); });
    if (zoomNext) zoomNext.addEventListener('click', (e) => { e.stopPropagation(); step(1); });
    zoomModal.addEventListener('click', (e) => {
      if (e.target === zoomModal || e.target === zoomImage) closeZoom();
    });

    document.addEventListener('keydown', (e) => {
      if (!zoomModal.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeZoom();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    });

    /* Swipe between photos inside the zoom on phones. */
    let zx = 0;
    zoomModal.addEventListener('touchstart', (e) => { zx = e.touches[0].clientX; }, { passive: true });
    zoomModal.addEventListener('touchend', (e) => {
      const d = zx - e.changedTouches[0].clientX;
      if (Math.abs(d) >= 50) step(d > 0 ? 1 : -1);
    }, { passive: true });
  }

  /**
   * Quantity controls (with max bound)
   */
  if (qtyInput) {
    if (qtyMinus) {
      qtyMinus.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10);
        if (val > 1) {
          qtyInput.value = val - 1;
          updateDelivery();
        }
      });
    }

    if (qtyPlus) {
      qtyPlus.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10) || 1;
        if (val < MAX_QUANTITY) {
          qtyInput.value = val + 1;
          updateDelivery();
        }
      });
    }

    qtyInput.addEventListener('change', () => {
      let val = parseInt(qtyInput.value, 10);
      if (isNaN(val) || val < 1) {
        val = 1;
      } else if (val > MAX_QUANTITY) {
        val = MAX_QUANTITY;
      }
      qtyInput.value = val;
      updateDelivery();
    });
  }

  /**
   * Back-in-stock notify — the "Notify Me" button sits inside the product
   * <form>, so it can't be its own <form>.
   *
   * C4 (12 Oct 2026): with a Klaviyo public site key (Theme settings →
   * Klaviyo, printed as data-klaviyo-key), the request goes to Klaviyo's
   * client back-in-stock endpoint for THIS size, which records "Subscribed
   * to Back in Stock" (the F1 flow's trigger). Only what the shopper typed
   * is sent — the email, the size's variant id and the optional tick — with
   * credentials omitted and no referrer, so it works before cookie consent
   * and sets no cookies. The optional "Also send me Mel's hair tips and
   * news" tick (unticked; consent option a2) subscribes the email to the
   * Klaviyo list. The salon still gets its /contact email for every request
   * (saying whether the box was ticked), so nobody is missed while F1 is a
   * draft and un-ticked requests get their one-off reply.
   *
   * Fallback (no key, or Klaviyo unreachable / refused): POST to Shopify's
   * /contact endpoint as before — the salon gets an email with the product
   * URL, customer address as reply-to. That is a success only on Shopify's
   * contact_posted=true redirect; a bot check (/challenge) or any other
   * redirect is reported honestly as not sent.
   */
  const notifyBtn = section.querySelector('[data-notify-submit]');
  if (notifyBtn) {
    const notifyMsgEl = section.querySelector('[data-notify-msg]');
    const notifyEmailEl = section.querySelector('[data-notify-email]');
    const notifyTipsEl = section.querySelector('[data-notify-tips]');
    const notifyLabel = notifyBtn.textContent;
    const klaviyoKey = notifyFormEl ? (notifyFormEl.getAttribute('data-klaviyo-key') || '').trim() : '';
    const klaviyoList = notifyFormEl ? (notifyFormEl.getAttribute('data-klaviyo-list') || '').trim() : '';
    const notifyArrives = !!(notifyFormEl && notifyFormEl.getAttribute('data-notify-when') === 'arrives');
    const KLAVIYO_REVISION = '2025-10-15';
    let notifySending = false;
    let notifyFor = variantIdInput ? String(variantIdInput.value) : '';

    // Klaviyo client API (public key only). Resolves on 2xx, else rejects.
    function klaviyoPost(path, payload) {
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), 10000) : null;
      return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(klaviyoKey), {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(payload),
        signal: ctrl ? ctrl.signal : undefined,
      }).then((r) => {
        if (timer) clearTimeout(timer);
        if (!r.ok) throw new Error('klaviyo ' + r.status);
        return r;
      }, (err) => {
        if (timer) clearTimeout(timer);
        throw err;
      });
    }

    function klaviyoBackInStock(email, variantId) {
      return klaviyoPost('back-in-stock-subscriptions', {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } },
          },
        },
      });
    }

    function klaviyoTips(email) {
      return klaviyoPost('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock form',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: klaviyoList } } },
        },
      });
    }

    // The salon inbox route (the pre-C4 behaviour).
    function contactNotify(email, note) {
      const body = new URLSearchParams();
      body.append('form_type', 'contact');
      body.append('utf8', '✓');
      body.append('contact[email]', email);
      body.append('contact[body]', 'Back-in-stock request: ' + document.title + ' — ' + window.location.href + (note ? '\n' + note : ''));
      return fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      }).then((r) => {
        if (!r.ok) throw new Error('status ' + r.status);
        const finalUrl = r.url || '';
        if (/\/challenge(\b|\/|\?|$)/.test(finalUrl)) throw new Error('challenge');
        if (r.redirected && !/[?&]contact_posted=true/.test(finalUrl)) throw new Error('redirected');
        return r;
      });
    }

    function clearNotifyError() {
      if (notifyEmailEl) {
        notifyEmailEl.classList.remove('tc-input--error');
        notifyEmailEl.removeAttribute('aria-invalid');
      }
      if (notifyMsgEl && notifyMsgEl.classList.contains('is-error')) {
        notifyMsgEl.textContent = '';
        notifyMsgEl.classList.remove('is-error');
      }
    }

    // A message belongs to the size it was written for: picking another size
    // clears an old error or "Thanks" and re-arms the button (QA Q5-07).
    // A request still in flight keeps its own outcome.
    document.addEventListener('variant:change', () => {
      const now = variantIdInput ? String(variantIdInput.value) : '';
      if (now === notifyFor || notifySending) return;
      notifyFor = now;
      clearNotifyError();
      if (notifyMsgEl) {
        notifyMsgEl.textContent = '';
        notifyMsgEl.classList.remove('is-success');
      }
      notifyBtn.disabled = false;
      notifyBtn.textContent = notifyLabel;
    });
    // Editing the address clears the "valid email" error.
    if (notifyEmailEl) notifyEmailEl.addEventListener('input', clearNotifyError);

    notifyBtn.addEventListener('click', () => {
      const emailEl = notifyEmailEl;
      const msgEl = notifyMsgEl;
      const email = emailEl ? emailEl.value.trim() : '';
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (msgEl) {
          msgEl.textContent = 'Please enter a valid email address.';
          msgEl.classList.remove('is-success');
          msgEl.classList.add('is-error');
        }
        if (emailEl) {
          emailEl.classList.add('tc-input--error');
          emailEl.setAttribute('aria-invalid', 'true');
          emailEl.focus();
        }
        return;
      }
      if (msgEl) msgEl.classList.remove('is-error', 'is-success');
      if (emailEl) {
        emailEl.classList.remove('tc-input--error');
        emailEl.removeAttribute('aria-invalid');
      }
      const original = notifyBtn.textContent;
      notifyBtn.disabled = true;
      notifyBtn.textContent = 'Sending…';
      notifySending = true;
      notifyFor = variantIdInput ? String(variantIdInput.value) : notifyFor;
      const variantId = variantIdInput ? String(variantIdInput.value) : '';
      const wantsTips = !!(notifyTipsEl && notifyTipsEl.checked);
      const tipsNote = 'Also send Mel\'s hair tips and news: ' + (wantsTips ? 'yes (ticked)' : 'no');

      const viaKlaviyo = (klaviyoKey && /^\d+$/.test(variantId))
        ? klaviyoBackInStock(email, variantId).then(() => {
          // The tick: best effort, the back-in-stock request itself has succeeded.
          const tips = (wantsTips && klaviyoList) ? klaviyoTips(email).catch(() => {}) : Promise.resolve();
          // The salon inbox keeps its copy of every request, as before C4, so
          // nobody is missed while F1 is a draft, and un-ticked requests (which
          // F1 can't email under consent a2) get their one-off reply. Best
          // effort: the shopper's request is already recorded in Klaviyo.
          return tips.then(() => contactNotify(email, tipsNote + '. Also recorded in Klaviyo back in stock.').catch(() => {}));
        })
        : Promise.reject(new Error('no klaviyo'));

      viaKlaviyo
        .catch(() => contactNotify(email, tipsNote))
        .then(() => {
          if (msgEl) {
            msgEl.textContent = notifyArrives
              ? "✓ Thanks — we'll email you when it arrives."
              : "✓ Thanks — we'll email you when it's back.";
            msgEl.classList.add('is-success');
          }
          if (emailEl) emailEl.value = '';
          if (notifyTipsEl) notifyTipsEl.checked = false;
          notifyBtn.textContent = 'Done ✓';
        })
        .catch(() => {
          if (msgEl) {
            msgEl.textContent = 'Sorry, that didn’t send — please email the salon and we’ll let you know.';
            msgEl.classList.add('is-error');
          }
          notifyBtn.disabled = false;
          notifyBtn.textContent = original;
        })
        .then(() => { notifySending = false; });
    });
  }

  /* ==========================================================================
     Takeover (13 Oct 2026): Subscribe & save 15% + The Color Circle cards
     (sections/product-main.liquid: [data-tc-lines] under the price and the
     [data-tc-buy] fieldset over the Shopify Subscriptions app block).
     - The app stays the ONE source of selling_plan. A card tap ticks the
       app's own radio in the visible section (checked + a bubbling change),
       then re-checks at 0 and 200 ms that TheColor.Cart.sellingPlanFrom()
       holds that plan (or null), re-applying once. If the form still
       disagrees, the cards go back to one-off, so the page never says
       "subscription" while the form would add one-off.
     - One-off is checked on every load. Subscribe & save is never chosen
       for the shopper; "or £41.61 with Subscribe & save ›" (here and in the
       sticky bar) scrolls to the card and focuses it without choosing it.
     - Size change: the app resets each size to one-time, so a plan the
       shopper picked is re-applied 200 ms after variant:change. A size
       without plans hides the cards and shows the Circle line instead.
     - Points: whole pounds × 2 until 11.59pm Sun 25 Oct 2026 (London =
       2026-10-25T23:59:00Z), then × the theme's points per £1. One-off only.
     - Sends tc:purchase {sns, price, weeks, eligible, snsPrice} for the
       sticky bar; the Add to Cart label reads "Add subscription · £41.61"
       only while the form really holds a plan.
     ========================================================================== */
  var tcBuyState = null;
  function tcAtcLabel(available) {
    if (!available) return 'Sold Out';
    var st = tcBuyState;
    if (st && st.sns && st.price > 0) return 'Add subscription · ' + formatMoney(st.price);
    return 'Add to Cart';
  }

  (function tcBuyCards() {
    const lines = section.querySelector('[data-tc-lines]');
    let buy = section.querySelector('[data-tc-buy]');
    if (!lines && !buy) return;

    const TC_FF_END = 1792972740000;
    function tcLeft() {
      const ms = TC_FF_END - Date.now();
      if (ms <= 0) return null;
      const m = Math.floor(ms / 60000);
      return { d: Math.floor(m / 1440), h: Math.floor(m % 1440 / 60), m: m % 60 };
    }
    const baseRate = Math.max(1, parseInt((lines || buy).getAttribute('data-base-rate'), 10) || 1);
    function rate() { return tcLeft() ? 2 : baseRate; }

    const hint = lines && lines.querySelector('[data-tc-sns-hint]');
    const cpl = lines && lines.querySelector('[data-tc-cpl]');
    const other = lines && lines.querySelector('[data-tc-sns-other]');
    const appWrap = section.querySelector('.tc-sns-skinned');

    /* The cards need the app's markup to drive. Without it (app off or not
       rendered) the cards stand down and the app block shows as it is. */
    let appOk = !!(buy && appWrap && appWrap.querySelector('.shopify_subscriptions_app_block'));
    if (buy && !appOk) {
      buy.hidden = true;
      if (appWrap) appWrap.classList.remove('tc-sns-skinned');
      buy = null;
    }

    const oneoffRadio = buy && buy.querySelector('input[name="tc_purchase"][value="oneoff"]');
    const snsRadio = buy && buy.querySelector('input[name="tc_purchase"][value="sns"]');
    const oneoffCard = oneoffRadio && oneoffRadio.closest('.tc-buy__card');
    const snsCard = snsRadio && snsRadio.closest('.tc-buy__card');
    const reveal = buy && buy.querySelector('[data-tc-reveal]');
    const planRadios = buy ? Array.prototype.slice.call(buy.querySelectorAll('input[name="tc_plan"]')) : [];
    const anyEligible = !!buy && (product.variants || []).some(function (v) {
      return v && v.selling_plan_allocations && v.selling_plan_allocations.length > 0;
    });

    /* Never open on a subscription (UK law: no pre-ticked subscriptions),
       even if the browser restored the radios from a previous visit. */
    if (oneoffRadio) oneoffRadio.checked = true;
    if (planRadios[0]) planRadios[0].checked = true;

    function allocFor(v, planId) {
      const list = (v && v.selling_plan_allocations) || [];
      if (!list.length) return null;
      if (planId) {
        for (let i = 0; i < list.length; i++) {
          if (String(list[i].selling_plan_id) === String(planId)) return list[i];
        }
      }
      return list[0];
    }
    function eligible(v) { return !!(buy && appOk && allocFor(v, null)); }
    function chosenPlan() {
      return planRadios.find(function (r) { return r.checked; }) || planRadios[0] || null;
    }
    function formPlan() {
      return TC.Cart && typeof TC.Cart.sellingPlanFrom === 'function' && form
        ? TC.Cart.sellingPlanFrom(form, section)
        : null;
    }
    function appSection(id) {
      const safe = String(id).replace(/\D/g, '');
      return safe ? section.querySelector('section.shopify_subscriptions_app_block[data-variant-id="' + safe + '"]') : null;
    }
    /* Tick the app's own radio for this plan (null = one-time purchase). */
    function drive(planId) {
      const v = liveVariant;
      const sec = v && appSection(v.id);
      if (!sec) return false;
      const input = planId
        ? sec.querySelector('input[data-radio-type="selling_plan"][data-selling-plan-id="' + String(planId).replace(/\D/g, '') + '"]')
        : sec.querySelector('input[data-radio-type="one_time_purchase"]');
      if (!input) return false;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    let applySeq = 0;
    function apply(planId) {
      const seq = ++applySeq;
      let retried = false;
      drive(planId);
      /* 0 ms: read; 200 ms: re-apply once if the form disagrees; 600 ms:
         still no plan in the form → the cards show one-off, which is what
         the form would add (the shopper can tap again). */
      function check(step) {
        if (seq !== applySeq) return;
        const want = planId ? String(planId) : '';
        const got = formPlan();
        if (String(got || '') !== want) {
          if (step === 1 && !retried) { retried = true; drive(planId); }
          else if (step === 2 && planId && oneoffRadio) {
            oneoffRadio.checked = true;
            drive(null);
          }
        }
        sync();
      }
      setTimeout(function () { check(0); }, 0);
      setTimeout(function () { check(1); }, 200);
      setTimeout(function () { check(2); }, 600);
    }

    function setAll(sel, fn) {
      section.querySelectorAll(sel).forEach(fn);
    }

    function sync() {
      const v = liveVariant;
      if (!v) return;
      const ff = !!tcLeft();
      // 20 Oct 2026: no Subscribe & save offer on a size that can't be bought.
      const el = eligible(v) && liveAvailable;
      const wantSns = !!(el && snsRadio && snsRadio.checked);
      const planR = chosenPlan();
      const a = el ? allocFor(v, wantSns && planR ? planR.value : null) : null;
      const snsPrice = a && a.price > 0 ? a.price : 0;
      const weeks = planR ? (planR.getAttribute('data-label') || '') : '';
      const pts = Math.floor((Number(v.price) || 0) / 100) * rate();
      const actual = formPlan();

      tcBuyState = { sns: !!(wantSns && actual && snsPrice), price: snsPrice, weeks: weeks };

      setAll('[data-tc-ff]', function (n) { n.hidden = !ff; });
      setAll('[data-tc-ff-after]', function (n) { n.hidden = ff; });
      if (snsPrice) setAll('[data-tc-sns-price]', function (n) { n.textContent = formatMoney(snsPrice); });
      setAll('[data-tc-oneoff-price]', function (n) { n.textContent = formatMoney(v.price); });
      setAll('[data-tc-pts]', function (n) { n.textContent = String(pts); });
      setAll('[data-tc-weeks]', function (n) { n.textContent = weeks; });
      setAll('[data-tc-oneoff-pts]', function (n) { n.hidden = pts < 1; });

      if (buy) buy.hidden = !el;
      if (reveal) reveal.hidden = !wantSns;
      if (oneoffCard) oneoffCard.classList.toggle('is-selected', !!(oneoffRadio && oneoffRadio.checked));
      if (snsCard) snsCard.classList.toggle('is-selected', !!(snsRadio && snsRadio.checked));
      planRadios.forEach(function (r) {
        const l = r.closest('.tc-buy__freq');
        if (l) l.classList.toggle('is-selected', r.checked);
      });
      if (hint) hint.hidden = !el;
      if (cpl) cpl.hidden = el || pts < 1 || !liveAvailable;
      if (other) other.hidden = el || !anyEligible;

      if (addBtn && !addBtn.disabled && !addBtn.classList.contains('is-pending')) {
        addBtn.textContent = tcAtcLabel(liveAvailable);
      }
      document.dispatchEvent(new CustomEvent('tc:purchase', {
        detail: {
          sns: tcBuyState.sns,
          price: tcBuyState.sns ? snsPrice : 0,
          weeks: weeks,
          eligible: el,
          snsPrice: el ? (allocFor(v, planR ? planR.value : null) || {}).price || 0 : 0
        }
      }));
    }

    if (buy) {
      buy.addEventListener('change', function (e) {
        const t = e.target;
        if (!t || (t.name !== 'tc_purchase' && t.name !== 'tc_plan')) return;
        if (t.name === 'tc_purchase') {
          if (t.value === 'sns') {
            const r = chosenPlan();
            if (reveal) reveal.hidden = false;
            apply(r ? r.value : null);
          } else {
            apply(null);
          }
        } else if (snsRadio && snsRadio.checked) {
          apply(t.value);
        }
        sync();
      });
    }

    /* "or £41.61 with Subscribe & save ›": to the card, focused, not chosen */
    function focusSubscribe() {
      if (!buy || buy.hidden || !snsRadio) return;
      const target = snsCard || buy;
      let reduce = false;
      try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (err) { reduce = false; }
      target.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      setTimeout(function () {
        try { snsRadio.focus({ preventScroll: true }); } catch (err) { snsRadio.focus(); }
        if (snsCard) {
          snsCard.classList.add('is-pointed');
          setTimeout(function () { snsCard.classList.remove('is-pointed'); }, 1600);
        }
      }, reduce ? 0 : 350);
    }
    if (hint) hint.addEventListener('click', focusSubscribe);
    TC.PdpBuy = { focusSubscribe: focusSubscribe };
    /* Email and banner links: /products/<handle>#subscribe scrolls to the
       Subscribe & save card and focuses it, but never selects it: one-off
       stays the default (pipeline audit, 13 Oct 2026). */
    if (/^#subscribe(-and-save)?$/i.test(window.location.hash || '')) {
      const goSubscribe = function () { setTimeout(focusSubscribe, 600); };
      if (document.readyState === 'complete') goSubscribe();
      else window.addEventListener('load', goSubscribe, { once: true });
    }

    /* Size change: prices now; the app has swapped its section by 200 ms,
       so a plan the shopper picked is re-applied then. A size without
       plans: the cards hide (the choice is kept for an eligible size) and
       the app goes back to one-time. */
    document.addEventListener('variant:change', function () {
      sync();
      setTimeout(sync, 0);
      setTimeout(function () {
        const v = liveVariant;
        if (eligible(v)) {
          if (snsRadio && snsRadio.checked) {
            const r = chosenPlan();
            apply(r ? r.value : null);
            return;
          }
          if (formPlan()) apply(null);
        } else if (buy) {
          drive(null);
        }
        sync();
      }, 200);
    });

    /* The Founding Fortnight ends while the page is open: re-paint once a
       minute until then (no seconds, no countdown here). */
    if (tcLeft()) {
      const timer = setInterval(function () {
        if (!tcLeft()) { clearInterval(timer); sync(); }
      }, 60000);
    }

    sync();
    if (document.readyState === 'complete') setTimeout(sync, 0);
    else window.addEventListener('load', function () { sync(); }, { once: true });
  })();

  /**
   * Add to Cart — delegates to centralised TheColor.Cart API. "Adding…" and
   * "Added ✓" stay at full strength (.is-pending); a failure is shown by
   * cart.js's toast (cart:error), and the button simply resets.
   */
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();

      if (!addBtn || addBtn.disabled) return;

      const variantId = variantIdInput ? parseInt(variantIdInput.value, 10) : null;
      const quantity = qtyInput ? Math.max(1, Math.min(MAX_QUANTITY, parseInt(qtyInput.value, 10) || 1)) : 1;

      if (!variantId) return;

      // Safety guard — fail gracefully if cart module hasn't loaded (e.g. network error)
      if (!TC.Cart || typeof TC.Cart.addToCart !== 'function') return;

      addBtn.disabled = true;
      addBtn.classList.add('is-pending');
      addBtn.textContent = 'Adding…';

      /* add_to_cart analytics are emitted by cart.js once the add has
         actually succeeded (CartActions.addToCart is the single funnel). */
      function reset() {
        addBtn.classList.remove('is-pending');
        addBtn.disabled = !liveAvailable;
        addBtn.textContent = tcAtcLabel(liveAvailable);
      }

      /* A partial add (QA Q5-06): Shopify added what it had, and cart.js
         fires cart:error {partial: true, added} yet still resolves, with the
         drawer showing Shopify's note. The quantity asked for can't be met
         any more (the basket now holds all there was), so the field goes
         back to 1 rather than reading 99 for the next tap. Only for this
         add, not for drawer or rail adds made meanwhile. */
      const onPartial = (ev) => {
        if (!(ev.detail && ev.detail.partial) || !qtyInput) return;
        qtyInput.value = 1;
        updateDelivery();
      };
      document.addEventListener('cart:error', onPartial);
      const settle = () => document.removeEventListener('cart:error', onPartial);

      /* Subscribe & save (S1): the plan picked in the Shopify Subscriptions
         app block (its hidden selling_plan input, copied into this form), or
         null for a one-off add, which then posts exactly as before. */
      const sellingPlan = typeof TC.Cart.sellingPlanFrom === 'function'
        ? TC.Cart.sellingPlanFrom(form, section)
        : null;

      TC.Cart.addToCart(variantId, quantity, {}, null, sellingPlan)
        .then(() => {
          settle();
          addBtn.textContent = 'Added ✓';
          setTimeout(reset, 2000);
        })
        .catch(() => {
          settle();
          reset();
        });
    });
  }
})();
